import { useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { PopoverArrow, PopoverContent, PopoverRoot, PopoverTrigger } from '@discover-ui/popover';
import { cn } from '@discover-lib/utils';

const HOVER_OPEN_DELAY_MS = 300;
const HOVER_CLOSE_DELAY_MS = 100;

// A mouse click also focuses the button, and that must not keep the tip open.
function isKeyboardFocus(element: HTMLElement) {
  try {
    return element.matches(':focus-visible');
  } catch {
    // Safari before 15.4 throws on :focus-visible, so treat focus as keyboard focus there.
    return true;
  }
}

export type TooltipProps = {
  content: string;
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
};

export default function Tooltip({
  content,
  children,
  className,
  'aria-label': ariaLabel,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const keyboardFocusedRef = useRef(false);

  const clearTimer = () => {
    if (timeoutRef.current == null) return;
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  };

  const changeOpen = (next: boolean) => {
    clearTimer();
    setOpen(next);
  };

  const changeOpenAfter = (next: boolean, delay: number) => {
    clearTimer();
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      setOpen(next);
    }, delay);
  };

  // Wait before opening so the tooltip does not flash while the pointer crosses the page.
  const handleHoverStart = (event: ReactPointerEvent) => {
    if (event.pointerType === 'mouse') changeOpenAfter(true, HOVER_OPEN_DELAY_MS);
  };

  // The short close delay lets the pointer cross the gap onto the tooltip itself.
  const handleHoverEnd = (event: ReactPointerEvent) => {
    // A keyboard-focused tooltip stays open until blur or Escape, wherever the mouse goes.
    if (event.pointerType === 'mouse' && !keyboardFocusedRef.current) {
      changeOpenAfter(false, HOVER_CLOSE_DELAY_MS);
    }
  };

  return (
    <PopoverRoot open={open} onOpenChange={changeOpen}>
      <PopoverTrigger
        aria-label={ariaLabel ?? content}
        aria-haspopup={undefined}
        className={cn(
          "relative inline-flex items-center justify-center border-0 bg-transparent p-0 before:absolute before:-inset-1 before:content-[''] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
          className
        )}
        onPointerEnter={handleHoverStart}
        onPointerLeave={handleHoverEnd}
        onPointerUp={(event) => {
          if (event.pointerType !== 'mouse') changeOpen(!open);
        }}
        onFocus={(event) => {
          if (!isKeyboardFocus(event.currentTarget)) return;
          keyboardFocusedRef.current = true;
          changeOpen(true);
        }}
        onBlur={() => {
          keyboardFocusedRef.current = false;
          changeOpen(false);
        }}
        // Opening is driven by hover, focus and touch above, not by the default click toggle.
        onClick={(event) => event.preventDefault()}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        role="tooltip"
        side="bottom"
        sideOffset={4}
        collisionPadding={8}
        // The tooltip must not take focus from the button, on open or on close.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onPointerEnter={handleHoverStart}
        onPointerLeave={handleHoverEnd}
        className="w-auto whitespace-nowrap rounded-xl border-0 bg-ds-gray-light-950 px-3 py-2 text-center text-2xs leading-4 text-ds-white">
        {content}
        <PopoverArrow width={16} height={6} className="fill-ds-gray-light-950" />
      </PopoverContent>
    </PopoverRoot>
  );
}
